import {
    DataGridPremiumProps,
    GridCellModes,
    GridCellModesModel,
    GridCellParams,
    GridColDef,
    GridFilterInputValueProps,
    GridRenderCellParams,
    GridRowHeightParams,
    MuiEvent,
    getGridSingleSelectOperators,
    getGridStringOperators,
    useGridApiRef,
    GridTreeDataGroupingCell,
} from "@mui/x-data-grid-premium";
import EntityTable, { CustomTogglePanelColDefinition } from "../entityTable/EntityTable";
import { IAttachment, IAttachmentThumbnail, IAttachmentExtendedRecord, AttachmentsPageResult } from "@shared/mirage/core/contracts/IAttachment";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import _, { isEqual } from "lodash";
import { PopUpPdfReader } from "../PdfReader/PopUpPdfReader";
import "./documentsTable.css";
import { Button, NativeSelect, TablePaginationBaseProps, Tooltip } from "@mui/material";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import LocalAttachmentsService from "@mirage/services/LocalAttachmentsService";
import InformDialog from "../informDialog/InformDialog";
import { DocumentsHistoryPanel } from "./DocumentsHistoryPanel";
import CustomDetailPanelToggle from "../entityTable/components/CustomDetailPanelToggle";
import DocumentsTablePreview from "./DocumentsTablePreview";
import { DocumentsTableFileSizeCell } from "./DocumentsTableFileSizeCell";
import { DocumentsTableActionCell } from "./DocumentsTableActionsCell";
import { Where } from "@shared/mirage/entityTable/Filter";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import { CustomGridToolbarOptions } from "../entityTable/components/CustomGridToolbar";
import useAlertService from "@mirage/hooks/useAlertService";
import AccessCell from "./AccessCell";
import FolderCell from "./FolderCell";
import MessageBox from "../warningBox/MessageBox";
import FileTypeCell from "./FileTypeCell";
import { TRAY, TRAY_NAMES } from "@shared/mirage/PickLists/TRAY";
import DropWrapper from "../dragAndDrop/DropWrapper";
import { EntityTableHelper } from "@shared/mirage/helpers/EntityTableHelper";
import { MirageConstants } from "@shared/mirage/mirageConstants";
import useUploadAttachments, { OnBeforeUploadReturns, UploadAttachment } from "./hooks/useUploadAttachments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import useDynamicHeight from "@mirage/hooks/useDynamicHeight";
import { SupersedingDialog } from "./SupersedingDialog";

export interface DocumentsTableDisplayOptions {
    entityRecord?: {
        entityName: string;
        recordId: number;
        tray?: TRAY_NAMES;
    };
    attachmentIds?: number[];
}
export interface DocumentsTableSupersedeSaveAttachmentOptions {
    entityName: string;
    recordId: number;
    kind?: string;
    tray?: string;
}

interface DocumentsTableProps {
    fileTypesMap?: Record<string, string[]>;
    publicOnly?: boolean;
    folders?: string[];
    stateKey: string;
    displayFor: DocumentsTableDisplayOptions;
    title?: string;
    forceFetch?: boolean;
    filter?: Where;
    supersedeSaveOptions?: DocumentsTableSupersedeSaveAttachmentOptions;
    readOnly?: boolean;
    noNew?: boolean;
    newAttachmentDefaults?: {
        folder?: string | null;
    };
    /**
     * If provided, no upload button will be shown and the upload will be handled by the consumer
     */
    customUpload?: UploadAttachment;
    onBeforeUpload?: (attachments: IAttachment[]) => OnBeforeUploadReturns;
    onRowsSelected?: (rows: any[]) => void;
    toolbarOptions?: CustomGridToolbarOptions;
    overrideColumns?: (existingColumns: GridColDef[]) => GridColDef[];
    customPageRequest?: (entityName: string, recordId: number, pr: IEntityPageRequest) => Promise<IEntityPageResult<IAttachmentThumbnail & IAttachmentExtendedRecord>>;
    onCellChanged?: () => void;
    overrideActions?: (actions: JSX.Element[], row: any & IAttachment) => JSX.Element[];
    showAll?: boolean;
}

export const DOCUMENTS_TABLE_ACTION_COLUMN_FIELD = "action" as const;

const DocumentsTable = memo((props: DocumentsTableProps) => {
    const {
        showAll,
        onRowsSelected,
        onCellChanged: onCellChangedCb,
        readOnly,
        publicOnly = false,
        folders = [],
        displayFor,
        customPageRequest,
        noNew,
        overrideActions,
        onBeforeUpload,
        customUpload,
        stateKey,
    } = props;
    const [requestedPdfUrl, setRequestedPdfUrl] = useState<string>("");
    const divRef = useRef(null); // Ref to track the div
    const calculatedHeight = useDynamicHeight(divRef);
    const numericHeight = calculatedHeight === "auto" ? 250 : parseInt(calculatedHeight, 10) || 250;
    const gridHeight = `${Math.max(numericHeight, 250)}px`;
    const inputRef = useRef<HTMLInputElement>(null);
    const allowAddNew = !props.readOnly && !props.publicOnly && !noNew;
    const service = useMemo(() => new LocalAttachmentsService(publicOnly), [publicOnly]);
    const [dialog, setDialog] = useState<{ open: boolean; mutation: string | JSX.Element }>({ open: false, mutation: "" });
    const [supersedingAttachment, setSupersedingAttachment] = useState<IAttachment | null>(null);
    const [supersedeCandidate, setSupersedeCandidate] = useState<IAttachment | null>(null);
    const [editingAttachment, setEditingAttachment] = useState<IAttachment | null>(null);
    const [forceFetch, setForceFetch] = useState(false);
    const initialRender = useRef(true);
    const reloadTable = useCallback(() => setForceFetch((prev) => !prev), []);
    const defaultUploadAttachment = useUploadAttachments({
        service,
        toggleForceFetch: reloadTable,
        attachmentSaveOptions: { parentEntityName: displayFor.entityRecord?.entityName, recordId: displayFor.entityRecord?.recordId },
        onBeforeUpload,
        disableAddNew: !allowAddNew,
    });
    const { inputFileRef, onDropFileUpload, uploadAttachmentInput, uploadingAttIsLoading } = customUpload ? customUpload : defaultUploadAttachment;
    const apiRef = useGridApiRef();
    const [fileTypes, setFileTypes] = useState<string[]>([]);
    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});
    const showAlert = useAlertService();
    const displayForCache = useRef(props.displayFor);
    const { isMobile } = useDeviceDetect();
    const displayForHasChanged = useMemo(() => isEqual(displayForCache.current, props.displayFor), [props.displayFor]);
    const getTableFilter = useCallback(() => {
        const filterTray = props.displayFor.entityRecord?.tray;
        if (filterTray) {
            return { AND: [{ tray: { equals: filterTray } }] };
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayForHasChanged]);

    const getEntityTableStateSuffix = useCallback(() => {
        if (displayFor.entityRecord) {
            return stateKey + "#" + displayFor.entityRecord.entityName + displayFor.entityRecord.recordId;
        }
        if (displayFor.attachmentIds) {
            return stateKey + "#" + displayFor.attachmentIds.length;
        }
        return stateKey;
    }, [displayFor.attachmentIds, displayFor.entityRecord, stateKey]);

    const getEditingCellMode = useCallback(
        (fieldName: string) => {
            const allColumns = apiRef.current.getAllColumns();
            const column = allColumns.filter((r) => r.field === fieldName)[0];
            if (column && column.editable) {
                return GridCellModes.Edit;
            }
            return GridCellModes.View;
        },
        [apiRef]
    );
    const handleCellClick = useCallback(() => {
        if (apiRef) {
            setTimeout(() => {
                if (onRowsSelected) {
                    const map = apiRef.current.getSelectedRows();
                    const rows = Array.from(map.values());
                    onRowsSelected(rows);
                }
            }, 1);
        }
    }, [apiRef, onRowsSelected]);
    const updateAttachmentInternal = useCallback(async () => {
        if (editingAttachment) {
            const errors = await service.ValidateAttachments([editingAttachment]);
            if (errors.length) {
                const error = errors.map((r) => r.field + ": " + r.errorMsg).join(", ");
                showAlert(error, "error");
            } else {
                await service.UpdateAttachments([editingAttachment]);
            }
            setEditingAttachment(null);
            switchGridToViewMode();
            setForceFetch(!forceFetch);
        }
    }, [editingAttachment, forceFetch, service, showAlert]);

    const handleDoubleCellClick = useCallback(
        (params: GridCellParams) => {
            if (readOnly || params.rowNode.type == "group") {
                return;
            }
            setCellModesModel((prevModel) => {
                const allRowsViewMode = Object.keys(prevModel).reduce(
                    (acc, id) => ({
                        ...acc,
                        [id]: Object.keys(prevModel[id]).reduce((acc2, field) => {
                            return { ...acc2, [field]: { mode: GridCellModes.View } };
                        }, {}),
                    }),
                    {}
                );
                return {
                    ...allRowsViewMode,
                    [params.id]: {
                        ...Object.keys(prevModel[params.id] || {}).reduce((acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }), {}),
                        [params.field]: { mode: getEditingCellMode(params.field) },
                    },
                };
            });
        },
        [getEditingCellMode, readOnly]
    );
    const renderEditableCellWithEditHint = useCallback((p: GridRenderCellParams) => {
        const editHintIcon = (
            <Tooltip title="Start typing to edit">
                <FontAwesomeIcon icon={MirageTheme.icons.editIcon} style={{ ...MirageTheme.icons.sizeS, paddingRight: "5px" }} />
            </Tooltip>
        );
        if (p.hasFocus && !props.readOnly) {
            return (
                <>
                    {editHintIcon}
                    {p.value}
                </>
            );
        } else {
            return p.value;
        }
    }, []);
    const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => setCellModesModel(newModel), []);
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);

    useEffect(() => {
        if (!initialRender.current) {
            setForceFetch((p) => !p);
        } else {
            initialRender.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayForHasChanged, props.forceFetch]);

    const fetchThumbnailsInLoop = useCallback(
        async (ids: number[]) => {
            service.GetAttachmentsThumbnails(ids).then((imgs) => {
                const responseIds = Object.keys(imgs);
                if (responseIds.length !== ids.length) {
                    const diff = _.difference(ids, responseIds.map(Number));
                    if (apiRef.current) {
                        setTimeout(() => {
                            fetchThumbnailsInLoop(diff);
                        }, 3000);
                    }
                }
                if (!apiRef.current) {
                    return;
                }
                const rowModels = apiRef.current.getRowModels();
                for (const id of responseIds) {
                    const row = rowModels.get(Number(id));
                    if (row) {
                        row.Thumbnail.PageThumbnails = [imgs[Number(id)]];
                    }
                }
                apiRef.current.updateRows(Object.values(rowModels));
            });
        },
        [apiRef, service]
    );

    const checkPreviews = useCallback(
        (pageResponse: IEntityPageResult<IAttachmentThumbnail & IAttachmentExtendedRecord>) => {
            const rows = pageResponse.records;
            if (!rows) {
                return;
            }
            const previewableTypes = ["pdf", "jpg", "jpeg", "png", "docx"];
            const previewableRows = rows.filter((r) => previewableTypes.includes(_.last(r.name.toLowerCase().split(".")) || ""));
            const emptyRowIds = previewableRows.filter((r) => !r?.Thumbnail?.PageThumbnails || r?.Thumbnail?.PageThumbnails.length === 0).map((r) => r.id);
            if (emptyRowIds.length > 0) {
                fetchThumbnailsInLoop(emptyRowIds);
            }
        },
        [fetchThumbnailsInLoop]
    );

    const pageRequestHandler = useCallback(
        async (pageRequest: IEntityPageRequest) => {
            const ensureEmptyFolderIsEmptyString = (records: IAttachment[]) => {
                records.forEach((r) => {
                    r.folder = r.folder || "";
                });
            };
            const filter = getTableFilter();
            const prFilter = pageRequest.filter;
            if (props.filter || filter) {
                const where = { AND: [EntityTableHelper.MergeWheres({ ...props.filter, ...filter }, prFilter.where)] };
                pageRequest.filter = { where, parts: [] };
            }
            let pageResponse = null;
            if (displayFor.entityRecord) {
                const entityRecord = displayFor.entityRecord;
                if (typeof customPageRequest == "function") {
                    pageResponse = await customPageRequest(entityRecord.entityName, entityRecord.recordId, pageRequest);
                    ensureEmptyFolderIsEmptyString(pageResponse.records);
                } else {
                    pageResponse = await service.GetAttachmentsPage(entityRecord.entityName, entityRecord.recordId, pageRequest);
                    ensureEmptyFolderIsEmptyString(pageResponse.records);
                    const fileTypes = (pageResponse as AttachmentsPageResult).fileTypes || [];
                    setFileTypes(fileTypes);
                }
            } else {
                if (!displayFor.attachmentIds) {
                    throw "DocumentsTable configuration is wrong. DisplayFor must be set to entityRecord or attachmentIds";
                }
                pageResponse = await service.GetAttachmentsGridByIds(displayFor.attachmentIds, pageRequest);
                ensureEmptyFolderIsEmptyString(pageResponse.records);
            }
            checkPreviews(pageResponse);
            return pageResponse;
        },
        [checkPreviews, customPageRequest, displayFor.attachmentIds, displayFor.entityRecord, getTableFilter, props.filter, service]
    );

    const getDetailPanelContent = React.useCallback<NonNullable<DataGridPremiumProps["getDetailPanelContent"]>>(
        ({ row }) => (
            <DocumentsHistoryPanel
                readOnly={props.readOnly}
                row={row}
                service={service}
                publicOnly={publicOnly}
                setRequestedPdfUrl={setRequestedPdfUrl}
                onListChanged={() => {
                    setForceFetch((p) => !p);
                }}
            />
        ),
        [props.readOnly, publicOnly, service]
    );
    const onCellChanged = useCallback(
        async (id: number, field: string, value: string, singleRowUpdate = false) => {
            if (!apiRef) {
                console.error("onCellChanged apiRef is null");
                return;
            }
            const attachmentsToUpdate: IAttachment[] = [];
            const row = apiRef.current.getRowModels().get(id);
            if (!row) {
                console.error("onCellChanged row is null");
                return;
            }
            const selectedRows = Array.from(apiRef.current.getSelectedRows().values());
            if (selectedRows.length === 1 && typeof selectedRows[0] === "undefined") {
                selectedRows.length = 0;
            }
            const editedRowSelectedAlso = Boolean(selectedRows.find((r) => r && r["id"] === id));
            if (!editedRowSelectedAlso || singleRowUpdate) {
                row[field] = value;
                attachmentsToUpdate.push(row as IAttachment);
            } else {
                for (const selectedRow of selectedRows) {
                    selectedRow[field] = value;
                    attachmentsToUpdate.push(selectedRow as IAttachment);
                }
            }
            const updateSucceed = await service.UpdateAttachments(attachmentsToUpdate);
            if (!updateSucceed) {
                showAlert("Failed to update attachments", "error");
                return;
            }
            showAlert("Attachments updated", "success");
            switchGridToViewMode();
            setForceFetch((p) => !p);
            onCellChangedCb && onCellChangedCb();
        },
        [apiRef, onCellChangedCb, service]
    );
    const processRowUpdate = useCallback(
        async (updatedRow: IAttachment, oldRow: IAttachment) => {
            if (updatedRow.description !== oldRow.description) {
                await onCellChanged(updatedRow.id, "description", updatedRow.description, true);
            }
            if (updatedRow.name !== oldRow.name) {
                await onCellChanged(updatedRow.id, "name", updatedRow.name, true);
            }
            return updatedRow;
        },
        [onCellChanged]
    );
    const columnVisibilityModel = useMemo(() => {
        return {
            createdByUserId: !publicOnly,
            action: false,
            folder: !publicOnly,
        };
    }, [publicOnly]);

    const onSupersedeClicked = useCallback(
        async (a: IAttachment) => {
            setSupersedingAttachment(a);
            const entityRecord = displayFor.entityRecord;
            if (entityRecord) {
                const allAttachments = await service.GetAttachments(entityRecord.entityName, entityRecord.recordId);
                const candidates = allAttachments.filter((r) => r.tray !== TRAY.Correspondence);
                setDialog({
                    open: true,
                    mutation: (
                        <SupersedingDialog
                            candidate={supersedeCandidate}
                            entityName={entityRecord.entityName}
                            recordId={entityRecord.recordId}
                            publicOnly={props.publicOnly || false}
                            onSupersedeCandidateClicked={(e) => setSupersedeCandidate(e)}
                            supersedingAttachment={a}
                        />
                    ),
                });
            } else {
                console.error("Supersede is not available for this attachment, because entityRecord is not set");
                showAlert("Supersede is not available for this attachment", "error");
            }
        },
        [displayFor.entityRecord, props.publicOnly, service, supersedeCandidate]
    );
    const customTogglePanelCol: CustomTogglePanelColDefinition = {
        headerName: "Show Children Button",
        renderCell: (params) => <CustomDetailPanelToggle show={isNeedToShowPlus(params)} id={params.id} value={params.value} />,
    };
    if (!props.displayFor.entityRecord?.recordId && !Array.isArray(props.displayFor.attachmentIds)) {
        const header = props.title ? props.title : "Documents";
        const message = "Adding new documents is not available for new record.";
        return <MessageBox header={header} type="information" message={message} />;
    }

    const newRecord =
        allowAddNew && !customUpload
            ? {
                  caption: "Attachment",
                  disabled: !!props.readOnly,
                  onClick: () => {
                      if (inputFileRef.current) {
                          inputFileRef.current.click();
                      }
                  },
                  isLoading: uploadingAttIsLoading,
              }
            : undefined;

    return (
        <div ref={divRef} style={{ maxHeight: gridHeight }}>
            <DropWrapper
                onDrop={onDropFileUpload}
                inputId={`${props.stateKey}_documentsTableDndInputId`}
                iconHidden
                multiple
                readonly={props.readOnly}
                wrapperClassName="documentsTable_dropFiles"
                wrapperIsOverClassName={props.readOnly ? "documentsTable_dropFiles--readonly" : "documentsTable_dropFiles--over"}
            >
                <EntityTable
                    constantPageSize={500}
                    forceFetch={forceFetch}
                    title={props.title ? props.title : "Documents"}
                    apiRef={apiRef}
                    disableFilterOnBaseColumns={true}
                    key={stateKey}
                    showAll={showAll}
                    getRowClassName={(params) => {
                        if (params.row.checksum === "corrupted") {
                            return "documentsTable__row--corrupted";
                        }
                        return "";
                    }}
                    stateKey={getEntityTableStateSuffix()}
                    getDetailPanelHeight={() => "auto"}
                    customTogglePanelCol={isMobile ? undefined : customTogglePanelCol}
                    getDetailPanelContent={isMobile ? undefined : getDetailPanelContent}
                    cellModesModel={cellModesModel}
                    editMode="cell"
                    onCellModesModelChange={handleCellModesModelChange}
                    onCellKeyDown={onCellKeyDown}
                    onCellEditStop={async (row, event) => {
                        await updateAttachmentInternal();
                    }}
                    onCellDoubleClick={handleDoubleCellClick}
                    onCellClick={handleCellClick}
                    onPageRequest={pageRequestHandler}
                    processRowUpdate={processRowUpdate}
                    rowGroupingModel={["folder"]}
                    defaultGroupingExpansionDepth={-1}
                    groupingColDef={{
                        headerName: "Action",
                        minWidth: 400,
                        cellClassName: "documentsTable__grouping_action_cell",
                        sortable: false,
                        renderCell: (p: any) => {
                            if (p.rowNode.type === "group") {
                                return <GridTreeDataGroupingCell {...p} />;
                            }
                            return (
                                <DocumentsTableActionCell
                                    readOnly={props.readOnly || false}
                                    publicOnly={publicOnly}
                                    row={p.row}
                                    onSupersedeClicked={onSupersedeClicked}
                                    setRequestedPdfUrl={setRequestedPdfUrl}
                                    onListChanged={reloadTable}
                                    service={service}
                                    getSelectedRows={() => apiRef.current.getSelectedRows()}
                                    overrideActions={overrideActions}
                                />
                            );
                        },
                        valueFormatter: (params) => {
                            if (params.value === null || params.value === undefined || params.value === "") {
                                return "[...]";
                            }
                            return params.value;
                        },
                        colSpan: ({ rowNode }) => (rowNode.type === "group" ? apiRef.current.getVisibleColumns().length : 1),
                    }}
                    isRowSelectable={(params) => {
                        const isGroupRow =
                            (params.row && typeof params.row === "object" && "__group__" in params.row) ||
                            Object.keys(params.row || {}).length === 0 ||
                            (typeof params.id === "string" && params.id.includes("auto-generated-group"));

                        return !isGroupRow;
                    }}
                    columns={getColumns()}
                    toolbarOptions={{
                        newRecord: newRecord,
                        disableFilter: false,
                        ...props.toolbarOptions,
                    }}
                    getRowHeight={(param: GridRowHeightParams) => {
                        const rowIds = apiRef.current.getAllRowIds();
                        const lastRowId = _.last(rowIds);
                        if (param.id === lastRowId) {
                            return 78;
                        }
                        return 40;
                    }}
                    noRecordsMessage={"No attachments found"}
                    columnVisibilityModel={columnVisibilityModel}
                    hideRowsPerPage={isMobile}
                    overridePaginationProps={isMobile ? overridePaginationProps : undefined}
                    gridMaxHeight={gridHeight}
                    showQuickSearch
                    quickSearchAutofocus={false}
                    quickSearchColumns={["name", "description"]}
                />
                <PopUpPdfReader pdfUrl={requestedPdfUrl} />
                <InformDialog dialogActions={getSupersedeDialogActions()} header={getSupersedingAttachmentHeder()} mutation={dialog.mutation} open={dialog.open} />
                <input type="file" hidden ref={inputRef} multiple={supersedeCandidate == null} onChange={async (e) => await onFileSelected(e)} />
                {uploadAttachmentInput}
            </DropWrapper>
        </div>
    );

    function switchGridToViewMode() {
        setCellModesModel((prevModel) => {
            const allRowsViewMode = Object.keys(prevModel).reduce(
                (acc, id) => ({
                    ...acc,
                    [id]: Object.keys(prevModel[id]).reduce((acc2, field) => {
                        return { ...acc2, [field]: { mode: GridCellModes.View } };
                    }, {}),
                }),
                {}
            );
            return allRowsViewMode;
        });
    }
    async function onCellKeyDown(p: GridCellParams, e: MuiEvent<React.KeyboardEvent<HTMLElement>>) {
        if (e.code == "Enter") {
            await updateAttachmentInternal();
            return;
        }
        if (e.code == "Escape") {
            setForceFetch(!forceFetch);
            switchGridToViewMode();
            return;
        }
    }

    function getSupersedeSaveOptions() {
        let supersedeOpt = props.supersedeSaveOptions;
        if (!supersedeOpt) {
            if (props.displayFor.entityRecord) {
                supersedeOpt = { entityName: props.displayFor.entityRecord?.entityName, recordId: props.displayFor.entityRecord?.recordId };
            }
        }
        if (!supersedeOpt) {
            throw "No save options given for DocumentsTable";
        }
        if (!supersedeOpt.entityName) {
            throw "No save options entityName given for DocumentsTable";
        }
        if (!supersedeOpt.recordId) {
            throw "No save options recordId given for DocumentsTable";
        }
        return supersedeOpt;
    }
    async function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        const supersedeSaveOptions = getSupersedeSaveOptions();
        const files = e.target.files;
        if (!files) {
            return;
        }
        const file = files[0];
        if (!file) {
            return;
        }
        const fileName = file["name"];
        const hasExtension = fileName.indexOf(".") > -1;
        if (!hasExtension) {
            showAlert("File must have an extension", "error");
        } else {
            if (supersedingAttachment) {
                const fileReader = new FileReader();
                fileReader.addEventListener("load", async (e) => {
                    if (e.target && e.target.result) {
                        const base64 = (e.target.result as string).split(",").pop();
                        await service.SupersedeWithFile(supersedingAttachment.id, {
                            base64: base64 as string,
                            entityName: supersedeSaveOptions.entityName,
                            recordId: supersedeSaveOptions.recordId,
                            fileName: fileName,
                        });
                        setForceFetch(!forceFetch);
                        setSupersedeCandidate(null);
                    }
                });
                fileReader.readAsDataURL(file);
            }
        }
    }
    function getSupersedeDialogActions() {
        return (
            <div className="buttonsContainer">
                <Button variant="outlined" onClick={supersedeWithSelectedAttachment} disabled={supersedeCandidate == null}>
                    Supersede with selected document
                </Button>
                <Button variant="outlined" onClick={supersedeWithFile}>
                    Supersede with file...
                </Button>
                <Button variant="outlined" onClick={closeSupersede}>
                    Cancel
                </Button>
            </div>
        );
    }
    async function supersedeWithSelectedAttachment() {
        if (supersedingAttachment && supersedeCandidate) {
            await service.Supersede(supersedingAttachment.id, supersedeCandidate.id);
            setForceFetch(!forceFetch);
            setSupersedeCandidate(null);
        }
        closeSupersede();
    }
    function supersedeWithFile() {
        if (supersedingAttachment && inputRef) {
            inputRef.current?.click();
        }
        closeSupersede();
    }
    function closeSupersede() {
        setDialog({ open: false, mutation: "" });
    }
    function getSupersedingAttachmentHeder(): string {
        if (supersedingAttachment !== null) {
            return "Superseding attachment " + supersedingAttachment.name;
        }
        return "";
    }

    function getPublicColumns(publicOnly: boolean): GridColDef[] {
        if (!publicOnly) {
            return [
                {
                    field: "access",
                    headerName: "Access",
                    flex: 0.5,
                    editable: false,
                    filterable: false,
                    renderCell: (p) => <AccessCell {...p} onCellChanged={onCellChanged} readOnly={readOnly} />,
                },
                {
                    field: "status",
                    headerName: "Status",
                    filterable: false,
                    flex: 1,
                    editable: !props.readOnly,
                    type: "singleSelect",
                    valueOptions: ["Unchecked", "Checked and satisfactory", "Checked and unsatisfactory"],
                    valueParser: async (value, params) => {
                        await onCellChanged(Number(params?.id), params?.field as string, value);
                        return value;
                    },
                },
            ];
        }
        return [];
    }

    function getColumns() {
        const nameColSize = isMobile ? { width: 180, flex: undefined } : undefined;
        const descriptionColSize = isMobile ? { width: 180, flex: undefined } : undefined;
        const fileTypeColSize = isMobile ? { width: 150, flex: undefined } : undefined;
        const lastModifiedTimeColSize = isMobile ? { width: 200, flex: undefined } : undefined;
        const previewColumn: GridColDef = {
            field: "preview",
            filterable: false,
            headerName: "Preview",
            sortable: false,
            renderCell: (p) => {
                if (p.rowNode.type === "group") {
                    return "";
                }
                return <DocumentsTablePreview params={p} apiRef={apiRef} onReviewRequesting={async (id) => await service.GetAttachmentPreview(id)} />;
            },
            width: 140,
            cellClassName: "documentsTable__previewCell",
        };
        const sizeColumn: GridColDef = {
            field: "bodyLength",
            filterable: false,
            sortable: !publicOnly,
            headerName: "Size",
            type: "string",
            renderCell: (p) => {
                if (p.rowNode.type === "group") {
                    return "";
                }
                return <DocumentsTableFileSizeCell val={p.value} />;
            },
            width: 100,
        };
        const nameColumn: GridColDef = {
            field: "name",
            headerName: "File Name",
            flex: 1,
            editable: !props.readOnly,
            sortable: !publicOnly,
            valueParser: nameValueParser,
            filterable: true,
            filterOperators: containsOnlyOperator(),
            renderCell: (p) => {
                if (p.rowNode?.type === "group" || (p.row && typeof p.row === "object" && "__group__" in p.row)) {
                    return null;
                }
                return p.value;
            },
            ...nameColSize,
        };
        const baseColumns = (
            [
                nameColumn,
                {
                    field: "description",
                    headerName: "Description",
                    flex: 1,
                    editable: !props.readOnly,
                    filterable: true,
                    sortable: !publicOnly,
                    filterOperators: containsOnlyOperator(),
                    renderCell: (p) => {
                        if (p.rowNode?.type === "group" || (p.row && typeof p.row === "object" && "__group__" in p.row)) {
                            return null;
                        }
                        return renderEditableCellWithEditHint(p);
                    },
                    ...descriptionColSize,
                },
                {
                    field: "fileType",
                    headerName: "File Type",
                    flex: 1,
                    editable: false,
                    filterable: true,
                    sortable: !publicOnly,
                    filterOperators: getFileTypeFilterOperators(),
                    type: "singleSelect",
                    valueOptions: () => {
                        return fileTypes;
                    },
                    renderCell: (p) => (
                        <FileTypeCell
                            {...p}
                            onCellChanged={onCellChanged}
                            readOnly={props.readOnly}
                            onFileTypesRequested={(folder: string) => {
                                return props.fileTypesMap?.[folder] || [];
                            }}
                        />
                    ),
                    ...fileTypeColSize,
                },
                isMobile ? undefined : previewColumn,
                isMobile ? undefined : sizeColumn,
                {
                    field: "folder",
                    headerName: "Folder",
                    filterOperators: limitOperatorsTo("contains", "isEmpty"),
                    sortable: !publicOnly,
                    flex: 1,
                    renderCell: (p) => {
                        if (p.colDef?.field === "__row_group_by_columns_group__") {
                            return <FolderCell {...p} onCellChanged={onCellChanged} readOnly={true} folders={folders} />;
                        }

                        if (p.rowNode.type === "group") {
                            return null;
                        } else {
                            return <FolderCell {...p} onCellChanged={onCellChanged} readOnly={readOnly} folders={folders} />;
                        }
                    },
                },
                ...getPublicColumns(publicOnly),
                { field: "lastModifiedTime", headerName: "Last Modified", type: "dateTime", flex: 0.5, filterable: false, sortable: !publicOnly, ...lastModifiedTimeColSize },
            ] as GridColDef[]
        ).filter(Boolean);
        if (typeof props.overrideColumns === "function") {
            return props.overrideColumns(baseColumns);
        }
        return baseColumns;
    }

    function containsOnlyOperator() {
        return getGridStringOperators().filter((r) => r.value === "contains");
    }
    function limitOperatorsTo(...operators: string[]) {
        return getGridStringOperators().filter((r) => operators.includes(r.value));
    }
    function getFileTypeFilterOperators() {
        const isOperator = getGridSingleSelectOperators().filter((r) => r.value === "is")[0];
        isOperator.InputComponent = (props: GridFilterInputValueProps) => {
            const { item, applyValue } = props;
            const isEmptyOption = MirageConstants.EmptyOptionLabel;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                if (typeof item.value === "undefined") {
                    applyValue({ ...item, value: fileTypes[0] });
                }
            }, []);
            return (
                <NativeSelect
                    sx={{
                        marginTop: "16px",
                    }}
                    value={item.value}
                    placeholder="Select..."
                    onChange={(e) => applyValue({ ...item, value: e.target.value })}
                >
                    {[...fileTypes, isEmptyOption].map((r, idx) => (
                        <option key={idx} value={r}>
                            {r}
                        </option>
                    ))}
                </NativeSelect>
            );
        };
        return [isOperator];
    }
    function nameValueParser(value: string, params?: GridCellParams) {
        if (!params) {
            return value;
        }
        const attachment = params.row as IAttachment;
        attachment.name = value;
        setEditingAttachment(attachment);
        return value;
    }

    function isNeedToShowPlus(params: GridRenderCellParams): boolean {
        return !!params.row.history && params.row.history.length > 0;
    }
}, isEqual);

export default DocumentsTable;
